import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import MentionText from "./MentionText";
import { roleLabel } from "../../utils/feedHelpers";

function timeAgo(ts) {
  if (!ts) return "";
  const m = Math.floor((Date.now() - Number(ts)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(Number(ts)).toLocaleDateString();
}

function initialsFor(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return ((parts[0] || "?")[0] || "?").toUpperCase().padEnd(2, "?");
}

// Props:
//   comment: full comment object
//   currentUser: { id, role, ... }
//   onEdit: () => void
//   onDelete: () => void
function CommentCard({ comment, currentUser, onEdit, onDelete }) {
  const [{ users }] = useStateValue();
  const isMe = comment.authorId && comment.authorId === currentUser?.id;
  const author = isMe
    ? currentUser
    : Array.isArray(users)
      ? users.find((u) => u.id === comment.authorId)
      : null;
  const authorPhoto = comment.authorPhoto || author?.profilepicture || "";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const isOwner = comment.authorId === currentUser?.id;
  const isAdmin = currentUser?.role === "Admin";
  const canEdit = isOwner;
  const canDelete = isOwner || isAdmin;

  return (
    <div className="flex items-start gap-2.5 py-2.5">
      {authorPhoto ? (
        <img
          src={authorPhoto}
          alt={comment.authorName || "User"}
          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-7 h-7 rounded-full bg-slate-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
          {initialsFor(comment.authorName)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-50 rounded-lg px-3 py-2 inline-block max-w-full">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[12.5px] font-semibold text-slate-900">
              {comment.authorName || "Unknown"}
            </span>
            {comment.authorRole && (
              <span className="text-[9px] uppercase tracking-wider font-semibold bg-white text-slate-600 px-1 py-0.5 rounded">
                {roleLabel(comment.authorRole)}
              </span>
            )}
          </div>
          <div className="text-[13px] text-slate-800 whitespace-pre-wrap leading-snug">
            <MentionText text={comment.text} />
          </div>
        </div>
        <div className="text-[10.5px] text-gray-500 mt-1 ml-1">
          {timeAgo(comment.createdAt)}
          {comment.updatedAt && (
            <span className="ml-2 text-gray-400">(edited)</span>
          )}
        </div>
      </div>
      {(canEdit || canDelete) && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-7 z-10 w-32 bg-white border border-gray-200 rounded-md shadow-lg py-1">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit?.();
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete?.();
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12px] text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                >
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommentCard;
